import { ConditionItem } from './condition-item';
import { ConditionTemplate } from './condition-template';
import { CoordinatorService } from './coordinator.service';
import { Script } from './script';
import { ScriptItem } from './script-x-item';

type CHOICE = { id: string; label: string; isSelected?: boolean };
type kindTp = 'value' | 'choice' | 'formula';

export type CIFPAR = {
    id?: string;
    title?: string;
    description?: string;
    kind?: kindTp;
    choiceLs?: CHOICE[];
    script?: string;
    isLoad?: boolean;
    editTemplate?: ConditionTemplate;
};

export class ConditionField {
    id: string;
    title: string;
    description: string;
    kind?: kindTp;
    choiceLs?: CHOICE[];
    script?: string;
    private _scriptFun?: Function;
    private _editedRadioChoiceId?: string;
    private _editTemplate?;

    private _editMap = new Map<string, any>();

    coords: CoordinatorService;

    constructor(
        public template: ConditionTemplate,
        { id, title, description, kind, choiceLs, script, editTemplate }: CIFPAR,
    ) {
        this.coords = template.coords;
        this.id = id || this.coords.uniqueId();
        this.title = title || '';
        this.description = description || '';
        this.kind = kind || 'value';
        this.choiceLs = choiceLs;
        this.script = script;
        this._compileScript();
        this._editTemplate = editTemplate;
    }

    conditionEditor() {
        this.coords.condition$.formulaEditor(this, () =>
            this.coords.binaryTree$.editDialog('ratings'),
        );
    }

    get editTitle() {
        return this._editMap.get('editTitle') || '';
    }

    set editTitle(v: string) {
        this._editMap.set('editTitle', v);
        this._editTemplate!.updateEditLs();
    }

    get editDescription() {
        return this._editMap.get('editDescription') || '';
    }

    set editDescription(v: string) {
        this._editMap.set('editDescription', v);
    }

    get editKind(): kindTp {
        return this._editMap.get('editKind') || 'value';
    }

    set editKind(v: string) {
        this._editMap.set('editKind', v);
    }

    get showEditDescription() {
        return this._editMap.has('showEditDescription');
    }

    set showEditDescription(v: boolean) {
        if (v) this._editMap.set('showEditDescription', true);
        else this._editMap.delete('showEditDescription');
    }

    get editChoiceLs() {
        return this._editMap.get('editChoiceLs') || [];
    }

    get hasDetail() {
        return ['choice', 'formula'].includes(this.editKind);
    }

    toggleEditDescription() {
        this.showEditDescription = !this.showEditDescription;
        this._markForCheck();
    }

    private _compileScript() {
        if (!this.script?.trim()) return;
        try {
            this._scriptFun = this.coords.utils$.compileScriptFunction(this.script);
        } catch (error) {
            console.error('CONDITION FIELD SCRIPT ERROR', this.script, error);
        }
    }

    scriptFunction() {
        if (!this.script) return () => {};
        return (this._scriptFun = this.coords.utils$.compileScriptFunction(this.script));
    }

    scriptValue({ item, conditionItem }: { item: ScriptItem; conditionItem?: ConditionItem }) {
        if (!this.script) return '';
        try {
            if (!this._scriptFun) this._compileScript();
            // console.log('SCRIPT VALUE:', item.id, item.title);
            return this._scriptFun && this._scriptFun(item, this.coords.dataset$.main.scriptClass);
        } catch (error) {
            console.error('FORMULA ERROR', this.script, error);
        }
        // return this.coords.script$.run(this.script);
    }

    choiceValue(cItm: ConditionItem) {
        const v = cItm.getValue(this);

        if (v)
            for (const ch of this.choiceLs || []) {
                if (v === ch.id) return ch.label;
            }
        return '—';
    }

    initEditMap() {
        this._editMap.clear();
        this._editMap.set('editTitle', this.title);
        this._editMap.set('editDescription', this.description);
        this._editMap.set('editKind', this.kind);

        this.choiceLs &&
            this._editMap.set(
                'editChoiceLs',
                this.choiceLs.map((ch) => ({
                    label: ch.label,
                    isEdited: false,
                    id: ch.id,
                })),
            );
    }

    save() {
        const dct: any = {
            id: this.id,
            title: this.title,
            description: this.description,
            kind: this.kind,
        };
        this.choiceLs && this.choiceLs.length && (dct.choiceLs = this.choiceLs);
        this.script && (dct.script = this.script);

        console.log('CHOICELS', this.choiceLs);
        return dct;
    }

    delete() {
        this._editMap.set('isDeleted', true);
        this._editTemplate?.editCheck$.next();
    }

    get isEditDeleted() {
        return this._editMap.has('isDeleted');
    }

    submitEdit() {
        this.title = this._editMap.get('editTitle')?.trim() || '';
        this.description = this._editMap.get('editDescription')?.trim() || '';
        this.kind = this._editMap.get('editKind') || 'value';
        if (this.kind === 'choice') {
            const choiceLs = this._editMap.get('editChoiceLs');
            this.choiceLs = (choiceLs?.filter((v: any) => v.label.trim() !== '') || []).map(
                (ch: any) => ch,
            );
        }
    }

    private _collectionXItemSetWithConditions(isPreview = false) {
        const s = new Set<string>();
        if (this.kind === 'formula') {
            if (isPreview)
                this.coords.dataset$.main.mainXitemLs.forEach((xitem) => {
                    if (this.scriptValue({ item: xitem.scriptClass })) s.add(xitem.id);
                });
            else s.add('_formula');
        } else
            this.coords.condition$.conditionItemLs.forEach((citm) => {
                if (citm.templateId !== this.template.id) return;
                if (citm.hasValue(this)) s.add(citm.itemId);
            });
        return s;
    }

    get count() {
        if (this.kind === 'formula') return this.coords.text('Calculate');
        return this._collectionXItemSetWithConditions().size;
    }

    testEdit(txt: string) {
        let count = 0;
        let hit = 0;
        let miss = 0;
        let errorNotBoolean = 0;
        const errorItemCalculateMap = new Map<string, Set<string>>();
        let compileError = '';

        try {
            const fun = new Function('$item', '$collection', txt);
            this.coords.dataset$.main.mainXitemLs.forEach((xitem) => {
                count++;
                try {
                    const item = xitem.scriptClass;
                    const resp = fun(item, this.coords.dataset$.main.scriptClass);
                    if (resp === true) hit++;
                    else if (resp === false) miss++;
                    else {
                        errorNotBoolean++;
                    }
                } catch (error: any) {
                    const k = error.toString();
                    let s = errorItemCalculateMap.get(k);
                    if (!s) {
                        s = new Set<string>();
                        errorItemCalculateMap.set(k, s);
                    }
                    s.add(xitem.id);
                }
            });
        } catch (error: any) {
            compileError = error.toString();
        }

        return {
            isCondition: true,
            count,
            hit,
            miss,
            errorNotBoolean,
            errorItemCalculateMap,
            compileError,
        };
    }

    preview(isAll = false) {
        const stg = this.coords.stage$.current!.tag;

        this.coords.resultList$.activateSubset({
            idSet: this._collectionXItemSetWithConditions(true),
            title: this.coords.text('conditionItems'),
            prefix: this.title,
            icon: 'value',
            class: 'selection-in-general',
            back: () => this.coords.stage$.set(stg),
            stage: 'collection',
        });
    }

    submitScript(data: string) {
        this.script = data;
        this._compileScript();
        this._markForCheck();
    }

    getScript() {
        return this.script || '';
    }

    private _markForCheck() {
        this._editTemplate!.editCheck$.next();
    }

    confirmEditedChoice(ch: any, e: Event) {
        e.preventDefault();
        e.stopPropagation();
        ch.isEdited = false;
        this._markForCheck();
    }

    submitItemEdit(citm: ConditionItem, value: any) {
        this.setValue(citm, value);
    }

    value(citm: ConditionItem, position: number) {
        const v = citm?.getValue(this);
        switch (this.kind) {
            case 'choice':
                if (v && v.length)
                    for (const ch of this.choiceLs || []) {
                        if (ch.id === v[0]) return ch.label;
                    }
                break;
        }
        return v || '—';
    }

    editedValue(citm: ConditionItem) {
        let v = citm?.getValue(this);

        switch (this.kind) {
            case 'choice':
                v === undefined && (v = []);
                if (v.length) {
                    for (const ch of this.choiceLs || []) {
                        ch.id === v[0] && (this._editedRadioChoiceId = ch.id);
                    }
                }
                break;
        }

        return v;
    }

    setValue(citm: ConditionItem, value: any) {
        citm?.setValue(this, value);
    }

    // if (this.kind === 'choice') value = [this._editedRadioChoiceId].filter((v) => v);
    addChoice(label: string) {
        let ls = this._editMap.get('editChoiceLs');
        if (!ls) {
            ls = [];
            this._editMap.set('editChoiceLs', ls);
        }
        for (let v of ls) {
            if (v.value === label) return;
        }
        ls.push({ label, isEdited: true, id: this.coords.uniqueId() });
        this._markForCheck();
    }

    editChoice(ch: any) {
        let ls: { id: string }[] = this._editMap.get('editChoiceLs')!;
        ls.forEach((origCh: any) => (origCh.isEdited = origCh === ch));
        this._markForCheck();
    }

    deleteChoice(ch: any) {
        let ls: { id: string }[] = this._editMap.get('editChoiceLs')!;
        ls = ls.filter((v) => v !== ch);
        this._editMap.set('editChoiceLs', ls);
        this._markForCheck();
    }

    plusChoice() {
        this.addChoice('');
        this._markForCheck();
    }

    move(tp: 'up' | 'down') {
        const ls = this._editTemplate!.editFieldLs;
        const i = ls!.indexOf(this);
        const j = tp == 'up' ? i - 1 : i + 1;
        ls?.splice(i, 1);
        ls?.splice(j, 0, this);
        this._markForCheck();
    }

    isDisabledMove(tp: 'up' | 'down') {
        const ls = this._editTemplate!.editFieldLs;
        return (tp == 'up' && ls[0] === this) || (tp == 'down' && ls[ls.length - 1] === this);
    }

    formulaEditor() {
        this.coords.display$.dialog$.next({
            action: 'formula-editor',
            target: this,
            backFun: () => this.coords.condition$.editDialog(this.template),
        });
    }
}
